import { useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { useAuth } from '../auth'
import { useI18n } from '../i18n'
import { hasBackend } from '../lib/supabase'
import { Button, Card, Field, Input, cx } from '../ui/kit'
import { Logo } from '../ui/Logo'
import { track } from '../lib/track'
import { kaufwegSichtbar } from '../lib/kaufweg'

export default function LoginView() {
  const { t, lang, setLang } = useI18n()
  const { signIn, signUp, enterDemo, error, clearError } = useAuth()
  const [tab, setTab] = useState<'in' | 'up'>('in')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [show, setShow] = useState(false)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)

  const de = lang === 'de'

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setNotice(null)
    clearError()
    try {
      if (tab === 'in') {
        await signIn(email.trim(), password)
      } else {
        // Ausgeblendet ist nicht abgeschaltet: der Reiter ist in der Hülle nicht
        // sichtbar, `tab` ließe sich aber trotzdem auf 'up' stehen haben.
        if (!kaufwegSichtbar()) return
        const { needsConfirmation } = await signUp(email.trim(), password)
        if (needsConfirmation) {
          setNotice(de
            ? 'Bestätigungs-E-Mail verschickt. Bitte den Link darin öffnen.'
            : 'Confirmation email sent. Please open the link in it.')
        }
      }
    } catch {
      // The message is surfaced through `error` from the auth context.
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="min-h-dvh flex items-center justify-center p-4">
      <div className="w-full max-w-sm space-y-4">
        <div className="text-center">
          <Logo size={60} className="mx-auto mb-3" />
          <h1 className="text-xl font-semibold">
            Stay<span className="text-accent">Suite</span>
          </h1>
          <p className="text-sm text-muted mt-1">
            {de
              ? 'Belegung, Preise, Angebote und Abrechnung für kleine Vermieter.'
              : 'Availability, rates, quotes and billing for small landlords.'}
          </p>
        </div>

        <Card className="p-4 space-y-3">
          {hasBackend ? (
            <>
              {/*
                * Der Reiter „30 Tage testen" fehlt in der App-Hülle.
                *
                * Guideline 3.1.3(f) trägt eine kostenlose Begleit-App zu einem
                * bezahlten Web-Werkzeug nur, solange die App selbst keinen
                * Kaufweg und keine Kontoanlage anbietet. Wer StaySuite im App
                * Store findet, meldet sich also im Browser an und hier nur noch
                * an — die Demo bleibt, und sie ist dort auch der bessere erste
                * Eindruck als ein Formular.
                *
                * Nebenwirkung, die niemanden überraschen soll: ohne Kontoanlage
                * greift auch die Löschpflicht aus 5.1.1(v) nicht.
                */}
              <div className={cx('flex gap-1 bg-surface-2 p-1 rounded-xl',
                !kaufwegSichtbar() && 'hidden')}>
                {(['in', 'up'] as const).map((k) => (
                  <button
                    key={k}
                    onClick={() => {
                      setTab(k); clearError(); setNotice(null)
                      // „Registrierung begonnen" ist der Wechsel auf diesen Reiter
                      // und nicht das Absenden: der Abstand zwischen begonnen und
                      // abgeschlossen ist genau die Zahl, die zeigt, ob das
                      // Formular jemanden verliert.
                      if (k === 'up') track('signup_start')
                    }}
                    className={cx('flex-1 py-2 rounded-lg text-sm',
                      tab === k ? 'bg-surface font-medium shadow-sm' : 'text-muted')}
                  >
                    {k === 'in'
                      ? (de ? 'Anmelden' : 'Sign in')
                      : (de ? '30 Tage testen' : 'Start 30-day trial')}
                  </button>
                ))}
              </div>

              <form onSubmit={submit} className="space-y-3">
                <Field label={de ? 'E-Mail' : 'Email'}>
                  <Input
                    type="email" required autoComplete="email" inputMode="email"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                  />
                </Field>
                <Field
                  label={de ? 'Passwort' : 'Password'}
                  hint={tab === 'up' ? (de ? 'Mindestens 8 Zeichen' : 'At least 8 characters') : undefined}
                >
                  <div className="relative">
                    <Input
                      type={show ? 'text' : 'password'}
                      required minLength={tab === 'up' ? 8 : undefined}
                      autoComplete={tab === 'in' ? 'current-password' : 'new-password'}
                      value={password} onChange={(e) => setPassword(e.target.value)}
                      className="pr-11"
                    />
                    <button
                      type="button"
                      onClick={() => setShow((s) => !s)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-muted"
                      aria-label={de ? 'Passwort anzeigen' : 'Show password'}
                    >
                      {show ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </Field>

                {error && (
                  <p className="text-sm bg-danger-soft text-danger rounded-xl px-3 py-2">{error}</p>
                )}
                {notice && (
                  <p className="text-sm bg-ok-soft text-ok rounded-xl px-3 py-2">{notice}</p>
                )}

                <Button type="submit" variant="primary" className="w-full" disabled={busy}>
                  {busy ? t('common.loading')
                    : tab === 'in' ? (de ? 'Anmelden' : 'Sign in')
                    : (de ? 'Konto anlegen' : 'Create account')}
                </Button>
              </form>
            </>
          ) : (
            <p className="text-sm text-muted">
              {de
                ? 'Kein Backend konfiguriert — die Demo läuft mit lokalen Beispieldaten.'
                : 'No backend configured — the demo runs on local sample data.'}
            </p>
          )}

          <div className="pt-1 border-t border-border">
            <Button variant="ghost" className="w-full" onClick={enterDemo}>
              {de ? 'Demo ohne Konto ansehen' : 'View the demo without an account'}
            </Button>
          </div>
        </Card>

        <div className="flex justify-center gap-1">
          {(['de', 'en'] as const).map((l) => (
            <button
              key={l}
              onClick={() => setLang(l)}
              className={cx('px-3 py-1 rounded-lg text-xs font-medium uppercase',
                lang === l ? 'bg-accent text-accent-fg' : 'bg-surface-2 text-muted')}
            >
              {l}
            </button>
          ))}
        </div>

        {/* Auch wer sich nicht anmelden kann, muss an Impressum und
            Datenschutzerklärung kommen — sonst hängt die Pflichtinformation an
            einem Passwort. */}
        <div className="flex justify-center gap-3 text-[11px] text-muted">
          <a href="/impressum" className="underline underline-offset-2">
            {de ? 'Impressum' : 'Legal notice'}
          </a>
          <a href="/datenschutz" className="underline underline-offset-2">
            {de ? 'Datenschutzerklärung' : 'Privacy notice'}
          </a>
          <a href="/agb" className="underline underline-offset-2">
            {de ? 'AGB' : 'Terms'}
          </a>
        </div>
      </div>
    </div>
  )
}
